import type { RadioFormData } from '../types';

interface SuccessScreenProps {
  formData: RadioFormData;
  isFree: boolean;
  isPaid: boolean;
  onGoToAdmin: () => void;
  onGoHome: () => void;
}

export default function SuccessScreen({ formData, isFree, isPaid, onGoToAdmin, onGoHome }: SuccessScreenProps) {
  const isManaged = formData.streamingMode === 'RFW_MANAGED';

  return (
    <div className="min-h-screen bg-background-100/70 flex flex-col">
      <div className="w-full px-4 md:px-8 py-4">
        <button
          onClick={onGoHome}
          className="flex items-center gap-2 text-sm text-foreground-500 hover:text-foreground-800 cursor-pointer transition-colors"
        >
          <i className="ri-arrow-left-line"></i>
          Volver al inicio
        </button>
      </div>

      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-lg bg-background-50 rounded-2xl border border-background-200 p-8 md:p-10 text-center">
          <div
            className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6 ${
              isFree ? 'bg-amber-100' : 'bg-green-100'
            }`}
          >
            <i
              className={`text-3xl font-bold ${isFree ? 'ri-time-line text-amber-600' : 'ri-check-line text-green-600'}`}
            ></i>
          </div>

          {isFree ? (
            <>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-3">
                ¡Solicitud enviada!
              </h2>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-sm font-semibold mb-4">
                <i className="ri-time-line"></i>
                Sujeto a aprobación
              </div>
              <p className="text-foreground-500 text-sm leading-relaxed mb-2">
                Tu radio{' '}
                <strong className="text-foreground-800">{formData.name || 'Radio'}</strong> ha
                quedado registrada. Al aprobarse, el sistema te asignará{' '}
                <strong className="text-foreground-800">tu Frecuencia RFW automáticamente</strong>.{' '}
                Después podrás solicitar una frecuencia especial o premium si querés.
              </p>
              <p className="text-foreground-400 text-xs leading-relaxed mb-3">
                Nuestro equipo revisará tu solicitud en las próximas 24 a 48 horas. Recibirás un
                correo cuando tu radio esté activa.
              </p>

              {/* Approval timeline */}
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-200 mb-4">
                <p className="text-xs font-semibold text-amber-800 mb-2">¿Qué sigue ahora?</p>
                <div className="space-y-2">
                  {isManaged ? (
                    <>
                      {[
                        { icon: 'ri-file-add-line', text: 'Tu solicitud entró a la cola de revisión' },
                        { icon: 'ri-eye-line', text: 'Un admin verifica tus datos y tu plan de streaming' },
                        { icon: 'ri-cloud-line', text: 'Si se aprueba, RFW prepara tu estación de streaming' },
                        { icon: 'ri-server-line', text: 'Aprovisionamiento: AutoDJ, almacenamiento y configuración' },
                        { icon: 'ri-mail-line', text: 'Recibís un correo con tu Stream URL y datos de acceso' },
                        { icon: 'ri-check-double-line', text: 'Tu radio queda activa con streaming administrado por RFW' },
                      ].map((step, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <div className="w-5 h-5 rounded-full bg-amber-200 flex items-center justify-center shrink-0 mt-0.5">
                            <i className={`${step.icon} text-amber-700 text-[9px]`}></i>
                          </div>
                          <span className="text-[11px] text-amber-800">{step.text}</span>
                        </div>
                      ))}
                    </>
                  ) : (
                    <>
                      {[
                        { icon: 'ri-file-add-line', text: 'Tu solicitud entró a la cola de revisión' },
                        { icon: 'ri-eye-line', text: 'Un admin verifica tus datos y stream URL' },
                        { icon: 'ri-check-double-line', text: 'Si todo está OK, se aprueba y reserva tu frecuencia' },
                        { icon: 'ri-mail-line', text: 'Recibís un correo con la confirmación' },
                      ].map((step, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <div className="w-5 h-5 rounded-full bg-amber-200 flex items-center justify-center shrink-0 mt-0.5">
                            <i className={`${step.icon} text-amber-700 text-[9px]`}></i>
                          </div>
                          <span className="text-[11px] text-amber-800">{step.text}</span>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>

              <div className="bg-red-50 rounded-xl p-3 border border-red-200 mb-6">
                <p className="text-[11px] text-red-600 leading-relaxed">
                  <i className="ri-error-warning-line mr-1"></i>
                  <strong>No todas las solicitudes son aceptadas.</strong> Asegurate de que tus datos sean reales{!isManaged && ' y tu stream funcione correctamente'} para mejorar tus chances.
                </p>
              </div>
            </>
          ) : (
            <>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-3">
                {isManaged ? '¡Tu radio está en proceso!' : '¡Tu radio está lista!'}
              </h2>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-50 border border-green-200 text-green-700 text-sm font-semibold mb-4">
                <i className="ri-flashlight-line"></i>
                {isManaged ? 'Activación con aprovisionamiento' : 'Activación inmediata con revisión posterior'}
              </div>
              <p className="text-foreground-500 text-sm leading-relaxed mb-2">
                <strong className="text-foreground-800">{formData.name || 'Radio'}</strong>{' '}
                {isManaged ? (
                  <>ya está registrada. RadioFrecuenciaWeb está preparando tu estación de streaming con AutoDJ, almacenamiento y configuración técnica según tu plan. El sistema te asignó tu <strong className="text-foreground-800">Frecuencia RFW automáticamente</strong>.</>
                ) : (
                  <>ya está activa. El sistema te asignó tu <strong className="text-foreground-800">Frecuencia RFW automáticamente</strong>. Si querés una frecuencia especial, podés solicitarla desde tu panel.</>
                )}
              </p>
              <p className="text-foreground-400 text-xs leading-relaxed mb-8">
                {isManaged ? (
                  <>Tu panel de administración está listo. Entrá para ver el estado de tu aprovisionamiento. Cuando tu estación esté lista, recibirás tu Stream URL pública. Nuestro equipo revisará tu contenido en las próximas horas.</>
                ) : (
                  <>Tu panel de administración está listo. Entra ahora y empeza a configurar tu radio. Nuestro equipo revisará tu contenido en las próximas horas para asegurar calidad en el directorio.</>
                )}
              </p>
            </>
          )}

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            {isFree && (
              <a
                href="/radio-application-status"
                className="w-full sm:w-auto px-8 py-3 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-xl transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-eye-line mr-2"></i>
                Ver estado de mi solicitud
              </a>
            )}
            {isPaid && (
              <button
                onClick={onGoToAdmin}
                className="w-full sm:w-auto px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-xl transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-primary-500/20"
              >
                {isManaged ? 'Ir al panel — Ver aprovisionamiento' : 'Ir al panel de administración'}
                <i className="ri-arrow-right-line ml-2"></i>
              </button>
            )}
            <button
              onClick={onGoHome}
              className="w-full sm:w-auto px-8 py-3 bg-background-50 hover:bg-background-100 text-foreground-700 text-sm font-semibold rounded-xl border border-background-200 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-3-line mr-2"></i>
              Volver al inicio
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}